import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Users,
  Heart,
  Key,
  TrendingUp,
  Loader2,
  Sparkles,
  Bug,
  Activity,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import AdminStatsTab from "../components/admin/AdminStatsTab";
import AdminUsersTab from "../components/admin/AdminUsersTab";
import AdminDonorsTab from "../components/admin/AdminDonorsTab";
import AdminPromotersTab from "../components/admin/AdminPromotersTab";
import AdminApiKeysTab from "../components/admin/AdminApiKeysTab";
import AdminWebhookErrorsTab from "../components/admin/AdminWebhookErrorsTab";
import AdminAiUsageTab from "../components/admin/AdminAiUsageTab";
import StoryHeader from "../components/admin/StoryHeader";
import RecentActivityPanel from "../components/admin/RecentActivityPanel";

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("stats");

  if (authLoading) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }
  // Server enforces the gate on every /api/admin/* call. Client-side gate
  // uses the `is_admin` flag from /auth/me (set only when email matches
  // ADMIN_EMAIL on the backend) — anyone else is bounced to the homepage.
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 md:py-12" data-testid="admin-dashboard">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">Admin</p>
        <h1 className="font-heading text-4xl mt-1">לוח הבקרה של המנהל</h1>
        <p className="text-sm text-muted-foreground mt-2">
          ניתוחי שימוש, ניהול משתמשים, תרומות ומפתחות בינה מלאכותית מרכזיים.
        </p>
      </header>

      {/* Story Header — the headline overview that sits above the
          tabs. Loads its own data on mount and is independent of the
          tab state below, so users see the command-center summary
          regardless of which legacy tab is active. */}
      <StoryHeader />

      {/* Recent activity — three live columns of signups/donations/clicks.
          Lives between the Story Header and the tabs as a continuously
          visible "what just happened" strip. */}
      <RecentActivityPanel />

      <Tabs value={tab} onValueChange={setTab} dir="rtl">
        <TabsList
          className="flex md:grid md:grid-cols-7 w-full bg-transparent border border-border/60 rounded-sm p-1 mb-6 overflow-x-auto md:overflow-visible"
          data-testid="admin-tabs"
        >
          <TabsTrigger
            value="stats"
            data-testid="admin-tab-stats"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <TrendingUp className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            סקירה
          </TabsTrigger>
          <TabsTrigger
            value="users"
            data-testid="admin-tab-users"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <Users className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            משתמשים
          </TabsTrigger>
          <TabsTrigger
            value="donors"
            data-testid="admin-tab-donors"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <Heart className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            תורמים
          </TabsTrigger>
          <TabsTrigger
            value="promoters"
            data-testid="admin-tab-promoters"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <Sparkles className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            מפיצים
          </TabsTrigger>
          <TabsTrigger
            value="api-keys"
            data-testid="admin-tab-api-keys"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <Key className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            מפתחות AI
          </TabsTrigger>
          <TabsTrigger
            value="ai-usage"
            data-testid="admin-tab-ai-usage"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <Activity className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            עלויות AI
          </TabsTrigger>
          <TabsTrigger
            value="webhook-errors"
            data-testid="admin-tab-webhook-errors"
            className="font-heading data-[state=active]:bg-gold/15 data-[state=active]:text-gold flex-shrink-0"
          >
            <Bug className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
            Webhook Errors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stats">
          <AdminStatsTab />
        </TabsContent>
        <TabsContent value="users">
          <AdminUsersTab />
        </TabsContent>
        <TabsContent value="donors">
          <AdminDonorsTab />
        </TabsContent>
        <TabsContent value="promoters">
          <AdminPromotersTab />
        </TabsContent>
        <TabsContent value="api-keys">
          <AdminApiKeysTab />
        </TabsContent>
        <TabsContent value="ai-usage">
          <AdminAiUsageTab />
        </TabsContent>
        <TabsContent value="webhook-errors">
          <AdminWebhookErrorsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
